import {
  createSaleSelfApi,
  createSaleSelfReRequestApi,
  deleteSaleSelfApi,
  downloadSaleFileApi,
  getSaleCompanyApi,
  getSaleSelfApi,
  getSaleSelfDetailApi,
  getSaleSelfItemApi,
  getSaleSelfTotalApi,
} from '@apis/sale_self';
import { Toast } from '@components/_atoms/Toast';
import { AxiosError } from 'axios';
import {
  createSaleSelfPayload,
  createSaleSelfReRequestPayload,
  downloadSaleFilePayload,
  getSaleSelfPaylaod,
  getSaleSelfPayload,
} from 'models/Supplement';
import {
  QuotationSaleGetSaleQuotationCompanyError,
  QuotationSaleGetSaleQuotationSelfError,
  QuotationSaleSelfCreateError,
  QuotationSaleSelfDeleteSaleQuotationSelfError,
  QuotationSaleSelfGetSaleQuotationItemSelfError,
  QuotationSaleSelfGetSaleQuotationTotalPriceSelfError,
  QuotationSaleSelfPostSaleQuotationReRequestError,
  QuotationSaleSelfReadError,
} from 'models/swagger/data-contracts';
import { useMutation, useQuery } from 'react-query';

/**
 *  자신의 판매 견적 요청서 가져오기 Fetch (프로젝트 관리)
 *  @function useGetSaleSelfFetch
 */
export const useGetSaleSelfFetch = ({ page, finish, project_name, rfq_number, is_company_project }: getSaleSelfPayload) => {
  const {
    data: getSaleSelfFetchData,
    isLoading: getSaleSelfIsLoading,
    isError: getSaleSelfIsError,
  } = useQuery(
    ['getSaleSelf', page, finish, project_name, rfq_number, is_company_project],
    async () => {
      const result = await getSaleSelfApi({
        page,
        finish,
        project_name,
        rfq_number,
        is_company_project,
      });
      return result?.data.data;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
      onError: (err: AxiosError<QuotationSaleGetSaleQuotationSelfError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );
  const isCustomLoading = (!getSaleSelfFetchData || getSaleSelfIsLoading) && !getSaleSelfIsError;

  return {
    getSaleSelfFetchData,
    isCustomLoading,
  };
};

/**
 *  판매자 - 회사의 판매 견적 요청서 가져오기 Fetch (프로젝트 관리)
 *  @function useGetSaleCompanyFetch
 */
export const useGetSaleCompanyFetch = ({ page, finish, project_name, rfq_number }: getSaleSelfPayload) => {
  const {
    data: getSaleCompanyFetchData,
    isLoading: getSaleCompanyIsLoading,
    isError: getSaleCompanyIsError,
  } = useQuery(
    ['getSaleCompany', page, finish, project_name, rfq_number],
    async () => {
      const result = await getSaleCompanyApi({
        page,
        finish,
        project_name,
        rfq_number,
      });
      return result?.data.data;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
      onError: (err: AxiosError<QuotationSaleGetSaleQuotationCompanyError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );
  const isCustomLoading = (!getSaleCompanyFetchData || getSaleCompanyIsLoading) && !getSaleCompanyIsError;

  return {
    getSaleCompanyFetchData,
    isCustomLoading,
  };
};


/**
 *  자신의 판매 견적 요청서 상세 가져오기 Fetch (구매용 견적서)
 *  @function useGetSaleSelfDetailFetch
 */
export const useGetSaleSelfDetailFetch = ({ rfq_number_id, sale_quotation_id }: getSaleSelfPaylaod) => {
  const {
    data: getSaleSelfDetailFetchData,
    isLoading: getSaleSelfDetailIsLoading,
    isError: getSaleSelfDetailIsError,
  } = useQuery(
    ['getSaleSelfDetail', rfq_number_id],
    async () => {
      const result = await getSaleSelfDetailApi({
        rfq_number_id,
        sale_quotation_id,
      });
      return result?.data.data;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
      onError: (err: AxiosError<QuotationSaleSelfReadError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );
  const isCustomLoading = (!getSaleSelfDetailFetchData || getSaleSelfDetailIsLoading) && !getSaleSelfDetailIsError;

  return {
    getSaleSelfDetailFetchData,
    isCustomLoading,
  };
};

/**
 *  판매자 - 판매 견적 요청서 생성 Fetch
 *  @function useCreateSaleSelfFetch
 */
export const useCreateSaleSelfFetch = () => {
  const { mutate: createSaleSelfMutate, isLoading: createSaleSelfIsLoading } = useMutation(
    ['createSaleSelf'],
    (props: createSaleSelfPayload) => createSaleSelfApi(props),
    {
      onError: (err: AxiosError<QuotationSaleSelfCreateError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    createSaleSelfMutate,
    createSaleSelfIsLoading,
  };
};

/**
 *  자신의 판매 견적 요청서 자재 리스트 가져오기 Fetch
 *  @function useGetSaleSelfItemFetch
 */
export const useGetSaleSelfItemFetch = ({ rfq_number_id, sale_quotation_id }: getSaleSelfPaylaod) => {
  const {
    data: getSaleSelfItemFetchData,
    isLoading: getSaleSelfItemIsLoading,
    isError: getSaleSelfItemIsError,
  } = useQuery(
    ['getSaleSelfItem', rfq_number_id],
    async () => {
      const result = await getSaleSelfItemApi({
        rfq_number_id,
        sale_quotation_id,
      });
      return result?.data.data;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
      onError: (err: AxiosError<QuotationSaleSelfGetSaleQuotationItemSelfError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );
  const isCustomLoading = (!getSaleSelfItemFetchData || getSaleSelfItemIsLoading) && !getSaleSelfItemIsError;

  return {
    getSaleSelfItemFetchData,
    isCustomLoading,
  };
};

/**
 *  자신의 판매 견적 요청서 자재별 총 금액 가져오기 Fetch
 *  @function useGetSaleSelfTotalFetch
 */
export const useGetSaleSelfTotalFetch = ({ rfq_number_id }: getSaleSelfPaylaod) => {
  const {
    data: getSaleSelfTotalFetchData,
    isLoading: getSaleSelfTotalIsLoading,
    isError: getSaleSelfTotalIsError,
  } = useQuery(
    ['getSaleSelfTotal', rfq_number_id],
    async () => {
      const result = await getSaleSelfTotalApi({
        rfq_number_id,
      });
      return result?.data.data;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
      onError: (err: AxiosError<QuotationSaleSelfGetSaleQuotationTotalPriceSelfError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );
  const isCustomLoading = (!getSaleSelfTotalFetchData || getSaleSelfTotalIsLoading) && !getSaleSelfTotalIsError;

  return {
    getSaleSelfTotalFetchData,
    isCustomLoading,
  };
};

/**
 *  자신의 판매 견적 요청서 삭제 Fetch
 *  @function useDeleteSaleSelfFetch
 */
export const useDeleteSaleSelfFetch = () => {
  const { mutate: deleteSaleSelfMutate } = useMutation(
    ['deleteSaleSelf'],
    ({ rfq_number_id }: getSaleSelfPaylaod) => deleteSaleSelfApi({ rfq_number_id }),
    {
      onError: (err: AxiosError<QuotationSaleSelfDeleteSaleQuotationSelfError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    deleteSaleSelfMutate,
  };
};

/**
 *  판매자 - 판매 견적 재요청 Fetch
 *  @function useCreateSaleSelfReRequestFetch
 */
export const useCreateSaleSelfReRequestFetch = () => {
  const { mutate: createSaleSelfReRequestMutate } = useMutation(
    ['createSaleSelfReRequest'],
    (props: createSaleSelfReRequestPayload) => createSaleSelfReRequestApi(props),
    {
      onError: (err: AxiosError<QuotationSaleSelfPostSaleQuotationReRequestError>) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    createSaleSelfReRequestMutate,
  };
};

/**
 *  거래문서 파일 다운로드 Fetch - 판매자
 *  @function useDownloadSaleFileFetch
 */
export const useDownloadSaleFileFetch = () => {
  const { mutate: downloadSaleFileMutate, isLoading } = useMutation(
    ['downloadSaleFile'],
    ({ rfq_number_id, type, sale_quotation_id }: downloadSaleFilePayload) => downloadSaleFileApi({ rfq_number_id, type, sale_quotation_id }),
    {
      onError: (err: any) => {
        if (err.response?.data?.message) {
          Toast.error(err.response.data.message.text);
        }
      },
    },
  );

  return {
    downloadSaleFileMutate,
    isLoading,
  };
};
